import React, { useMemo, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  Stack,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';

interface RequestEntry {
  property: string;
  value: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface LMSResultsProps {
  data: {
    response: Record<string, unknown> | RequestEntry[] | null;
    lmsCodeName: string;
    error?: string;
    submissionDate?: string | null;
    // request can be either the original object or already an array of entries
    request: Record<string, unknown> | RequestEntry[] | null;
  } | null;
}

interface ValueNodeProps {
  value: unknown;
  depth?: number;
  label?: string; // optional override label (e.g. property key or index)
}

const ValueNode: React.FC<ValueNodeProps> = ({ value, depth = 0, label }) => {
  const [open, setOpen] = React.useState(depth < 1); // auto-open first level

  // Primitive or null-ish rendering
  if (value === null || value === undefined || typeof value !== 'object') {
    if (value === null || value === undefined || value === '') {
      return (
        <Tooltip title="No value (null or empty)">
          <Chip size="small" color="warning" label="NULL" />
        </Tooltip>
      );
    }
    return (
      <>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {label ? `${label}: ` : ''} {String(value)} ({typeof value})
        </Typography>
      </>
    );
  }

  // Date instance special-case
  if (value instanceof Date) {
    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {value.toISOString()} (Date)
      </Typography>
    );
  }

  const isArray = Array.isArray(value);
  const entries: Array<{ k: string; v: unknown }> = isArray
    ? (value as unknown[]).map((v, idx) => ({ k: `[${idx}]`, v }))
    : Object.keys(value as Record<string, unknown>).map(k => ({ k, v: (value as any)[k] }));

  const summary = isArray ? `${label} tree (${(value as unknown[]).length})` : `atributes (${entries.length})`;

  return (
    <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { opacity: 0.85 },
        }}
        onClick={() => setOpen(o => !o)}
      >
        <IconButton size="small" sx={{ mr: 0.5 }} aria-label="toggle-nested-value">
          {open ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
        </IconButton>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {summary}
        </Typography>
      </Box>
      {open && entries.length > 0 && (
        <Box sx={{ pl: 3, borderLeft: '1px dotted', ml: 1, mt: 0.5 }}>
          {entries.map(child => (
            <Box key={child.k} sx={{ mb: 0.5 }}>
              <ValueNode value={child.v} depth={depth + 1} label={child.k} />
            </Box>
          ))}
        </Box>
      )}
      {open && entries.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          (empty)
        </Typography>
      )}
    </Box>
  );
};

export default function LMSResults({ data }: LMSResultsProps) {
  const { response, request, lmsCodeName, submissionDate, error } = data || {};

  // Reusable normalization helper (object or already entries)
  const toEntries = useCallback((src: Record<string, unknown> | RequestEntry[] | null | undefined): RequestEntry[] => {
    if (!src) return [];
    if (Array.isArray(src)) return src as RequestEntry[];
    const walk = (obj: any, prefix = ''): RequestEntry[] =>
      Object.keys(obj || {}).flatMap(key => {
        const value = (obj as any)[key];
        const path = prefix ? `${prefix}.${key}` : key;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return walk(value, path);
        }
        return [{ property: path, value }];
      });
    return walk(src as Record<string, unknown>);
  }, []);

  // Request entries
  const entries: RequestEntry[] = useMemo(() => toEntries(request), [request, toEntries]);

  // Group by top-level segment before first dot
  const grouped = useMemo(() => {
    const map: Record<string, RequestEntry[]> = {};
    entries.forEach(e => {
      const top = e.property.split('.')[0];
      map[top] = map[top] || [];
      map[top].push(e);
    });
    // Sort groups alphabetically and entries by property
    return Object.keys(map)
      .sort()
      .map(group => ({
        group,
        items: map[group].sort((a, b) => a.property.localeCompare(b.property)),
      }));
  }, [entries]);

  // State for expanded groups (derive default expanded if few groups)
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>(() => ({}));
  const toggle = (group: string) => setExpanded(prev => ({ ...prev, [group]: !prev[group] }));

  // Filtering by property/value text
  const [filter, setFilter] = React.useState('');
  const filteredGrouped = useMemo(() => {
    if (!filter) return grouped;
    const f = filter.toLowerCase();
    const match = (val: unknown): boolean => {
      if (val === null || val === undefined) return false;
      if (typeof val === 'object') {
        try {
          return JSON.stringify(val).toLowerCase().includes(f);
        } catch {
          return false;
        }
      }
      return String(val).toLowerCase().includes(f);
    };
    return grouped
      .map(g => ({
        ...g,
        items: g.items.filter(i => i.property.toLowerCase().includes(f) || match(i.value)),
      }))
      .filter(g => g.items.length > 0);
  }, [filter, grouped]);

  // Clear filter handler: resets filter text and collapses all expanded groups for a clean view.
  const handleClearFilter = () => {
    setFilter('');
    setExpanded({});
  };

  // CSV generation for entries array
  const generateCSV = useCallback(() => {
    const header = 'group,property,value';
    const lines = entries.map(e => {
      const group = e.property.split('.')[0];
      const serialize = (v: unknown) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'object') {
          try {
            return JSON.stringify(v);
          } catch {
            return '[unserializable object]';
          }
        }
        return String(v);
      };
      const safe = (raw: string) => `"${raw.replace(/"/g, '""')}"`;
      return [group, e.property, safe(serialize(e.value))].join(',');
    });
    return [header, ...lines].join('\n');
  }, [entries]);

  const handleDownloadCSV = () => {
    const csv = generateCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'lms_request_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalNulls = useMemo(() => entries.filter(e => e.value === null || e.value === undefined).length, [entries]);

  // ================= Response section =================
  const responseEntries: RequestEntry[] = useMemo(() => toEntries(response), [response, toEntries]);

  const groupedResponse = useMemo(() => {
    const map: Record<string, RequestEntry[]> = {};
    responseEntries.forEach(e => {
      const top = e.property.split('.')[0];
      map[top] = map[top] || [];
      map[top].push(e);
    });
    return Object.keys(map)
      .sort()
      .map(group => ({
        group,
        items: map[group].sort((a, b) => a.property.localeCompare(b.property)),
      }));
  }, [responseEntries]);

  const [expandedResp, setExpandedResp] = React.useState<Record<string, boolean>>(() => ({}));
  const toggleResp = (group: string) => setExpandedResp(prev => ({ ...prev, [group]: !prev[group] }));

  const [filterResp, setFilterResp] = React.useState('');
  const filteredGroupedResp = useMemo(() => {
    if (!filterResp) return groupedResponse;
    const f = filterResp.toLowerCase();
    const match = (val: unknown): boolean => {
      if (val === null || val === undefined) return false;
      if (typeof val === 'object') {
        try {
          return JSON.stringify(val).toLowerCase().includes(f);
        } catch {
          return false;
        }
      }
      return String(val).toLowerCase().includes(f);
    };
    return groupedResponse
      .map(g => ({
        ...g,
        items: g.items.filter(i => i.property.toLowerCase().includes(f) || match(i.value)),
      }))
      .filter(g => g.items.length > 0);
  }, [filterResp, groupedResponse]);

  const handleClearFilterResp = () => {
    setFilterResp('');
    setExpandedResp({});
  };

  const generateCSVResp = useCallback(() => {
    const header = 'group,property,value';
    const lines = responseEntries.map(e => {
      const group = e.property.split('.')[0];
      const serialize = (v: unknown) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'object') {
          try {
            return JSON.stringify(v);
          } catch {
            return '[unserializable object]';
          }
        }
        return String(v);
      };
      const safe = (raw: string) => `"${raw.replace(/"/g, '""')}"`;
      return [group, e.property, safe(serialize(e.value))].join(',');
    });
    return [header, ...lines].join('\n');
  }, [responseEntries]);

  const handleDownloadCSVResp = () => {
    const csv = generateCSVResp();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'lms_response_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalNullsResp = useMemo(
    () => responseEntries.filter(e => e.value === null || e.value === undefined).length,
    [responseEntries]
  );

  return (
    <Paper sx={{ padding: 2 }}>
      <Container maxWidth="xl">
        <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 500, mr: 1 }}>
          Sent to:{' '}
          {lmsCodeName &&
            lmsCodeName
              .replace(/_/g, ' ')
              .toLowerCase()
              .replace(/\b(\w)/g, (char: string) => char.toUpperCase())}
        </Typography>
        <Box mb={2} display="flex" alignItems="center">
          <CalendarTodayIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500, mr: 1 }}>
            Submission date:
          </Typography>
          <Typography variant="body2" color="text.primary">
            {submissionDate ? new Date(submissionDate).toLocaleString() : '-'}
          </Typography>
        </Box>
        {error ? (
          <Box mb={2}>
            <Alert severity="error">
              <b>Error occurred:</b> <span>{error}</span>
            </Alert>
          </Box>
        ) : (
          <Box mb={2}>
            <Alert severity="success">
              <b>Submitted via API</b>
            </Alert>
          </Box>
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          mb={2}
        >
          <TextField
            size="small"
            placeholder="Filter by property or value"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 4, opacity: 0.6 }} />,
            }}
            sx={{ width: { xs: '100%', sm: 320 } }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color="primary" label={`Fields: ${entries.length}`} />
            <Chip size="small" color={totalNulls ? 'warning' : 'success'} label={`Null/Empty: ${totalNulls}`} />
            <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadCSV}>
              Download CSV
            </Button>
            {/* Clear Filter: only shown when a filter is active; resets text filter and collapses groups */}
            {filter && (
              <Button variant="outlined" size="small" onClick={handleClearFilter} color="secondary">
                Clear Filter
              </Button>
            )}
          </Stack>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small" aria-label="LMS request data table">
            <TableHead>
              <TableRow>
                <TableCell width={60}>API Request Data Mapped</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGrouped.map(g => (
                <React.Fragment key={g.group}>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell colSpan={3} sx={{ py: 0.5 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton size="small" onClick={() => toggle(g.group)} aria-label="toggle group rows">
                            {expanded[g.group] ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {g.group} <Chip size="small" label={g.items.length} sx={{ ml: 1 }} />
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ padding: 0, borderBottom: 'none' }} colSpan={3}>
                      <Collapse in={expanded[g.group]} timeout="auto" unmountOnExit>
                        <Table size="small" aria-label={`${g.group} entries`}>
                          <TableBody>
                            {g.items.map(item => {
                              const propPathParts = item.property.split('.').slice(1); // remove group prefix
                              const subPath = propPathParts.join('.') || item.property;
                              const isNull = item.value === null || item.value === undefined || item.value === '';
                              return (
                                <TableRow key={item.property} hover>
                                  <TableCell
                                    width={60}
                                    sx={{ borderBottom: '1px dashed', fontSize: '0.75rem', color: 'text.secondary' }}
                                  >
                                    {g.group}
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: '1px dashed' }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {subPath}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ borderBottom: '1px dashed' }}>
                                    {isNull ? (
                                      <Tooltip title="No value (null or empty)">
                                        <Chip size="small" color="warning" label="NULL" />
                                      </Tooltip>
                                    ) : typeof item.value === 'object' ? (
                                      <ValueNode value={item.value} label={item.property} />
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                                      >
                                        {String(item.value)} ({typeof item.value})
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {filteredGrouped.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No matching fields.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {responseEntries.length > 0 && (
          <Box mt={4}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              API Response Data
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              mb={2}
            >
              <TextField
                size="small"
                placeholder="Filter response by property or value"
                value={filterResp}
                onChange={e => setFilterResp(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 4, opacity: 0.6 }} />,
                }}
                sx={{ width: { xs: '100%', sm: 360 } }}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" color="primary" label={`Fields: ${responseEntries.length}`} />
                <Chip
                  size="small"
                  color={totalNullsResp ? 'warning' : 'success'}
                  label={`Null/Empty: ${totalNullsResp}`}
                />
                <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadCSVResp}>
                  Download CSV
                </Button>
                {filterResp && (
                  <Button variant="outlined" size="small" onClick={handleClearFilterResp} color="secondary">
                    Clear Filter
                  </Button>
                )}
              </Stack>
            </Stack>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small" aria-label="LMS response data table">
                <TableHead>
                  <TableRow>
                    <TableCell width={60}>API Response Data Mapped</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGroupedResp.map(g => (
                    <React.Fragment key={g.group}>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell colSpan={3} sx={{ py: 0.5 }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="center" gap={1}>
                              <IconButton
                                size="small"
                                onClick={() => toggleResp(g.group)}
                                aria-label="toggle group rows"
                              >
                                {expandedResp[g.group] ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </IconButton>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {g.group} <Chip size="small" label={g.items.length} sx={{ ml: 1 }} />
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ padding: 0, borderBottom: 'none' }} colSpan={3}>
                          <Collapse in={expandedResp[g.group]} timeout="auto" unmountOnExit>
                            <Table size="small" aria-label={`${g.group} response entries`}>
                              <TableBody>
                                {g.items.map(item => {
                                  const propPathParts = item.property.split('.').slice(1); // remove group prefix
                                  const subPath = propPathParts.join('.') || item.property;
                                  const isNull = item.value === null || item.value === undefined || item.value === '';
                                  return (
                                    <TableRow key={item.property} hover>
                                      <TableCell
                                        width={60}
                                        sx={{
                                          borderBottom: '1px dashed',
                                          fontSize: '0.75rem',
                                          color: 'text.secondary',
                                        }}
                                      >
                                        {g.group}
                                      </TableCell>
                                      <TableCell sx={{ borderBottom: '1px dashed' }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                          {subPath}
                                        </Typography>
                                      </TableCell>
                                      <TableCell sx={{ borderBottom: '1px dashed' }}>
                                        {isNull ? (
                                          <Tooltip title="No value (null or empty)">
                                            <Chip size="small" color="warning" label="NULL" />
                                          </Tooltip>
                                        ) : typeof item.value === 'object' ? (
                                          <ValueNode value={item.value} label={item.property} />
                                        ) : (
                                          <Typography
                                            variant="body2"
                                            sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                                          >
                                            {String(item.value)} ({typeof item.value})
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                  {filteredGroupedResp.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No matching response fields.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Container>
    </Paper>
  );
}
